import { find, memoize, map, filter, get, isEmpty } from 'lodash';

import { BREADCRUMB_NAVIGATION_LINK_TYPES } from 'constants/breadcrumbNavigation';
import Log from 'utils/Log';
import Settings from 'utils/Settings';

class Breadcrumbs {
  static _generateLink = ({ handle, type }) => {
    switch (type) {
      case BREADCRUMB_NAVIGATION_LINK_TYPES.PRODUCT:
        return `/products/${handle}`;
      case BREADCRUMB_NAVIGATION_LINK_TYPES.COLLECTION:
        return `/collections/${handle}`;
      default:
      case BREADCRUMB_NAVIGATION_LINK_TYPES.HOME:
        return `/`;
    }
  };

  // Build a non filtered breadcrumb trail to connect siblings correctly
  // used for getSecondaryNavigationOptions() method
  static _getBreadcrumbTrail = ({ handle, type }) => {
    Log.debug('uncached _getBreadcrumbTrail');

    let breadcrumbTrail = [];

    const findNodeRecursively = node => {
      const isMatch = currentNode => {
        // Match Home on type alone
        if (
          currentNode.type === BREADCRUMB_NAVIGATION_LINK_TYPES.HOME &&
          currentNode.type === type
        ) {
          return true;
        }

        // Match other nodes on type + handle
        return currentNode.type === type && currentNode.handle === handle;
      };

      // If current node matches queried node, prepend current node to trail of breadcrumbs
      if (isMatch(node)) {
        breadcrumbTrail.unshift({ ...node, isActive: true });
        return node;
      }

      // See if any children nodes are matches
      const childMatch = find(node.subNavigations, subNavigation =>
        findNodeRecursively({ ...subNavigation })
      );

      // If current node is an ancestor of queried node, prepend current node to trail of breadcrumbs
      if (childMatch) {
        breadcrumbTrail.unshift(node);
      }

      return childMatch;
    };

    findNodeRecursively(Settings.breadcrumbNavigationData);

    // If no match is found for queried node, fallback to "Home / <queried node>" trail
    if (isEmpty(breadcrumbTrail)) {
      breadcrumbTrail = [Settings.breadcrumbNavigationData, { handle, type, isActive: true }];
    }

    // Normalize data
    const normalizedBreadcrumbTrail = map(breadcrumbTrail, breadcrumb => ({
      ...breadcrumb,
      linkUrl: Breadcrumbs._generateLink(breadcrumb),
    }));

    return { breadcrumbTrail: normalizedBreadcrumbTrail };
  };

  // Build a filtered breadcrumb trail with ghost nodes removed for getBreadcrumbTrail
  // used for getBreadcrumbTrail() method
  static _getGhostlessBreadcrumbTrail = ({ handle, type }) => {
    Log.debug('uncached _getGhostlessBreadcrumbTrail');

    let breadcrumbTrail = [];

    const findNodeRecursively = node => {
      const isMatch = currentNode => {
        // Match Home on type alone
        if (
          currentNode.type === BREADCRUMB_NAVIGATION_LINK_TYPES.HOME &&
          currentNode.type === type
        ) {
          return true;
        }

        // Match other nodes on type + handle
        return currentNode.type === type && currentNode.handle === handle;
      };

      // If current node matches queried node, prepend current node to trail of breadcrumbs
      if (isMatch(node)) {
        breadcrumbTrail.unshift({ ...node, isActive: true });
        return node;
      }

      // See if any children nodes are matches
      const childMatch = find(node.subNavigations, subNavigation =>
        findNodeRecursively({ ...subNavigation })
      );

      // If current node is an ancestor of queried node, prepend current node to trail of breadcrumbs
      if (childMatch) {
        breadcrumbTrail.unshift(node);
      }

      return childMatch;
    };

    findNodeRecursively(Settings.breadcrumbNavigationData);

    // If no match is found for queried node, fallback to "Home / <queried node>" trail
    if (isEmpty(breadcrumbTrail)) {
      breadcrumbTrail = [Settings.breadcrumbNavigationData, { handle, type, isActive: true }];
    }

    // Normalize data
    const normalizedBreadcrumbTrail = map(breadcrumbTrail, breadcrumb => ({
      ...breadcrumb,
      linkUrl: Breadcrumbs._generateLink(breadcrumb),
    }));

    const ghostlessBreadcrumbTrail = filter(normalizedBreadcrumbTrail, option => {
      return option.type !== BREADCRUMB_NAVIGATION_LINK_TYPES.GHOST;
    });

    return { breadcrumbTrail: ghostlessBreadcrumbTrail };
  };

  static getBreadcrumbTrail = memoize(Breadcrumbs._getGhostlessBreadcrumbTrail, JSON.stringify);

  static _getSecondaryNavigationOptions = ({ handle, type }) => {
    Log.debug('uncached _getSecondaryNavigationOptions');
    const { breadcrumbTrail } = Breadcrumbs._getBreadcrumbTrail({ handle, type });

    // Secondary navigation options are subnavigations of the level 1 navigation node in the current trail of breadcrumbs
    const secondaryNavigationOptions = get(breadcrumbTrail, [1, 'subNavigations'], []);

    const featuredSecondaryNavigationOptions = filter(secondaryNavigationOptions, {
      isFeatured: true,
    });

    const normalizedFeaturedSecondaryNavigationOptions = map(
      featuredSecondaryNavigationOptions,
      option => ({
        ...option,
        linkUrl: Breadcrumbs._generateLink(option),
        isActive: option.type === type && option.handle === handle,
      })
    );

    return { secondaryNavigationOptions: normalizedFeaturedSecondaryNavigationOptions };
  };

  static getSecondaryNavigationOptions = memoize(
    Breadcrumbs._getSecondaryNavigationOptions,
    JSON.stringify
  );
}

export default Breadcrumbs;
